export type Locale = "en" | "fr";

export type LocalizedText = {
  readonly en: string;
  readonly fr: string;
};

export type SiteCta = {
  readonly label: LocalizedText;
  readonly shortLabel: LocalizedText;
  readonly destination: string;
  readonly external: boolean;
};

export type SiteService = {
  readonly slug: string;
  readonly href: string;
  readonly name: LocalizedText;
  readonly description: LocalizedText;
};

export type SiteIndustry = {
  readonly slug: string;
  readonly href: string;
  readonly label: LocalizedText;
};

export const SITE_URL = "https://vert-ai.com";
export const SITE_NAME = "Vert AI";
export const SITE_LOGO_IMAGE = `${SITE_URL}/vert-logo.png`;
export const SITE_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const BOOKING_URL = "https://calendly.com/d/cvpp-rwv-v5j";
export const CONTACT_EMAIL = "guillaume.belisle@vert-ai.com";
export const FOUNDER_NAME = "Guillaume Belisle";
export const LINKEDIN_URL = "https://www.linkedin.com/company/vert-ai";

export const siteContent = {
  brand: {
    companyName: SITE_NAME,
    shortPositioning: {
      en: "Production AI, data, and decision systems for operators who need measurable business value — not AI theatre.",
      fr: "Systèmes d’IA, de données et de décision en production pour les opérateurs qui ont besoin de valeur mesurable, pas de théâtre IA.",
    },
    longPositioning: {
      en: "Vert AI is a founder-led team that scopes, builds, and deploys production AI systems, data platforms, workflow automations, and decision engines around real workflows, review paths, monitoring, ownership, and measurable operating value.",
      fr: "Vert AI est une équipe dirigée par son fondateur qui cadre, construit et déploie des systèmes IA de production, plateformes de données, automatisations de flux et moteurs de décision autour de vrais flux, chemins de revue, monitoring, responsabilité et valeur opérationnelle mesurable.",
    },
    audience: {
      en: "mid-market and enterprise operators, founders, COOs, CTOs, data leaders, operations leaders, and business-unit leaders.",
      fr: "opérateurs mid-market et enterprise, fondateurs, COOs, CTOs, leaders données, responsables opérations et responsables d’unites d’affaires.",
    },
    geography: {
      en: "Canada-based, serving North American companies.",
      fr: "Basee au Canada, au service d’entreprises nord-americaines.",
    },
    geographyUsageGuidance: {
      en: "Do not over-emphasize location unless useful for SEO/contact.",
      fr: "Ne pas surexposer la localisation sauf lorsque c’est utile pour le SEO ou le contact.",
    },
  },
  ctas: {
    primary: {
      label: {
        en: "Book a production AI fit call",
        fr: "Planifier un appel de cadrage IA",
      },
      shortLabel: {
        en: "Book a fit call",
        fr: "Planifier un appel",
      },
      destination: BOOKING_URL,
      external: true,
    },
    secondary: {
      label: {
        en: "Run the AI readiness scorecard",
        fr: "Faire le scorecard de préparation IA",
      },
      shortLabel: {
        en: "AI readiness scorecard",
        fr: "Scorecard IA",
      },
      destination: "/ai-readiness-scorecard",
      external: false,
    },
  },
  messagePillars: [
    {
      en: "Choose the right workflow before building.",
      fr: "Choisir le bon flux de travail avant de construire.",
    },
    {
      en: "Do not automate workflows before the risk, owner, and review path are clear.",
      fr: "Ne pas automatiser un flux avant que le risque, le responsable et le chemin de revue soient clairs.",
    },
    {
      en: "Fix the data foundation required for useful AI.",
      fr: "Corriger la fondation de données requise pour une IA utile.",
    },
    {
      en: "Build production systems, not demos.",
      fr: "Construire des systèmes de production, pas des démos.",
    },
    {
      en: "Measure business value and adoption.",
      fr: "Mesurer la valeur commerciale et l’adoption.",
    },
    {
      en: "Operate with evaluation, monitoring, human review, and governance.",
      fr: "Opérer avec évaluation, monitoring, revue humaine et gouvernance.",
    },
  ],
  services: [
    {
      slug: "production-ai-systems",
      href: "/services/production-ai-systems",
      name: {
        en: "Production AI Systems",
        fr: "Systèmes d’IA de production",
      },
      description: {
        en: "Production AI systems that move from workflow selection to shipped capability, with evaluation, observability, human review, security review, ownership, and business measurement built in.",
        fr: "Systèmes IA de production qui passent de la sélection du flux à une capacité livrée, avec évaluation, observabilité, revue humaine, revue sécurité, responsabilité et mesure business intégrées.",
      },
    },
    {
      slug: "agentic-ai-workflows",
      href: "/services/agentic-ai-workflows",
      name: {
        en: "Agentic AI & Workflow Automation",
        fr: "IA agentique et automatisation des flux",
      },
      description: {
        en: "Bounded agents and workflow automations designed around real operating constraints, human review, escalation paths, auditability, and measurable throughput.",
        fr: "Agents bornes et automatisations de flux conçus autour de vraies contraintes opérationnelles, de la revue humaine, des chemins d’escalade, de l’auditabilité et du debit mesurable.",
      },
    },
    {
      slug: "data-platforms",
      href: "/services/data-platforms",
      name: {
        en: "Data Platforms for AI",
        fr: "Plateformes de données pour l’IA",
      },
      description: {
        en: "Data foundations that make AI reliable: pipelines, models, quality checks, lineage, access rules, governance, and activation layers.",
        fr: "Fondations de données qui rendent l’IA fiable : pipelines, modèles, contrôles de qualité, lignage, règles d’accès, gouvernance et couches d’activation.",
      },
    },
    {
      slug: "decision-intelligence",
      href: "/services/decision-intelligence",
      name: {
        en: "Decision Intelligence",
        fr: "Intelligence décisionnelle",
      },
      description: {
        en: "Systems that combine data, rules, AI, workflow logic, thresholds, and feedback loops to improve recurring business decisions.",
        fr: "Systèmes qui combinent données, règles, IA, logique de flux, seuils et boucles de feedback pour améliorer les décisions business récurrentes.",
      },
    },
    {
      slug: "ai-readiness-audit",
      href: "/services/ai-readiness-audit",
      name: {
        en: "AI Readiness & Opportunity Mapping",
        fr: "Diagnostic de préparation IA et cartographie des opportunités",
      },
      description: {
        en: "A focused diagnostic to rank AI opportunities by workflow value, data readiness, implementation risk, ownership, and whether the first production path is worth scoping.",
        fr: "Un diagnostic ciblé pour classer les opportunités IA selon valeur du flux, préparation des données, risque d’implémentation, responsabilité et si un premier chemin de production mérite d’être cadré.",
      },
    },
  ] satisfies SiteService[],
  industries: [
    {
      slug: "retail-ecommerce",
      href: "/industries/retail-ecommerce-ai",
      label: {
        en: "Retail & Ecommerce",
        fr: "Commerce de detail et ecommerce",
      },
    },
    {
      slug: "cpg",
      href: "/industries/cpg-ai",
      label: {
        en: "CPG",
        fr: "Biens de consommation",
      },
    },
    {
      slug: "franchise-multi-location",
      href: "/industries/franchise-multi-location-ai",
      label: {
        en: "Franchise & Multi-location Operators",
        fr: "Franchises et reseaux multi-sites",
      },
    },
    {
      slug: "professional-services",
      href: "/industries/professional-services-ai",
      label: {
        en: "Professional Services",
        fr: "Services professionnels",
      },
    },
    {
      slug: "b2b-services",
      href: "/industries/b2b-services-ai",
      label: {
        en: "B2B Services",
        fr: "Services B2B",
      },
    },
    {
      slug: "accounting-firms",
      href: "/industries/accounting-firms",
      label: {
        en: "Accounting Firms",
        fr: "Cabinets comptables",
      },
    },
    {
      slug: "operations-heavy-mid-market",
      href: "/industries/manufacturing-distribution",
      label: {
        en: "Manufacturing & Distribution",
        fr: "Fabrication et distribution",
      },
    },
  ] satisfies SiteIndustry[],
  proof: {
    founderLedDelivery: {
      en: "Founder-led scoping, architecture, and implementation with senior technical involvement close to production decisions.",
      fr: "Cadrage, architecture et implémentation dirigés par le fondateur, avec implication technique senior près des décisions de production.",
    },
    productionExperience: {
      en: "Production AI, ML, data platform, decision-system, and automation experience applied from strategy through build.",
      fr: "Expérience en IA de production, ML, plateformes de données, systèmes décisionnels et automatisation, appliquée de la stratégie à la construction.",
    },
    selectedOperatorExperience: {
      en: "Founder experience includes designing and shipping production AI, ML, and data systems in operator environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware.",
      fr: "L’expérience fondateur inclut la conception et la livraison de systèmes IA, ML et données en production dans des environnements opérateurs incluant Louis Vuitton, Canada Goose, Lowe’s et Home Hardware.",
    },
  },
  trustStandards: [
    {
      en: "Evaluation harnesses",
      fr: "Harnais d’évaluation",
    },
    {
      en: "Prompt and workflow standards",
      fr: "Standards de prompts et de flux",
    },
    {
      en: "Human-in-the-loop controls",
      fr: "Contrôles avec humain dans la boucle",
    },
    {
      en: "Escalation logic",
      fr: "Logique d’escalade",
    },
    {
      en: "Observability and monitoring",
      fr: "Observabilité et monitoring",
    },
    {
      en: "Privacy and security review",
      fr: "Revue confidentialité et sécurité",
    },
    {
      en: "Data quality checks",
      fr: "Contrôles de qualité des données",
    },
    {
      en: "Ownership and governance",
      fr: "Responsabilité et gouvernance",
    },
    {
      en: "ROI and adoption measurement",
      fr: "Mesure du ROI et de l’adoption",
    },
  ],
  seo: {
    defaultTitle: "Production AI, Data & Decision Systems | Vert AI",
    defaultDescription:
      "Vert AI scopes, builds, and deploys production AI systems, data platforms, workflow automations, and decision engines with controls, ownership, and measurable value.",
    defaultImage: SITE_OG_IMAGE,
    defaultImageAlt: "Vert AI production AI systems",
  },
} as const;

export function localize(text: LocalizedText, locale: Locale = "en") {
  return text[locale] || text.en;
}

export function getCtaLabel(
  cta: SiteCta,
  locale: Locale = "en",
  variant: "label" | "shortLabel" = "label",
) {
  return localize(cta[variant], locale);
}
